import React, { useState } from 'react';
import { Trophy, Handshake, Calendar, Target, Save, Loader2, Building2, Megaphone } from 'lucide-react';
import { api } from '../../services/apiClient';

interface Props {
  application: any;
  onSaved: () => void;
}

const PARTNERSHIP_TIERS = [
  { value: 'sponsor_główny', label: 'Sponsor Główny', color: 'bg-amber-500', price: '50 000+ PLN' },
  { value: 'sponsor_premium', label: 'Sponsor Premium', color: 'bg-gray-400', price: '25 000 PLN' },
  { value: 'sponsor', label: 'Sponsor', color: 'bg-orange-700', price: '10 000 PLN' },
  { value: 'partner', label: 'Partner', color: 'bg-blue-500', price: '5 000 PLN' },
  { value: 'patron_medialny', label: 'Patron medialny', color: 'bg-purple-500', price: 'Barter' },
];

const BENEFIT_OPTIONS = [
  'Logo na koszulkach', 'Logo na stronie', 'Banner na stadionie', 'Posty social media',
  'Loza VIP (10 os.)', 'Zaproszenia na eventy', 'Naming rights', 'Roll-up na meczach',
  'Artykuł sponsorski', 'Newsletter mention', 'Spot video LED', 'Koszulki z logo (50 szt.)',
];

export const HutnikBiznesPanel: React.FC<Props> = ({ application, onSaved }) => {
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    partnership_tier: application.partnership_tier || '',
    contract_value: application.contract_value || '',
    contract_start: application.contract_start || '',
    contract_end: application.contract_end || '',
    selected_benefits: application.selected_benefits || [],
    sponsorship_goal: application.sponsorship_goal || '',
    event_interest: application.event_interest || '',
    contact_person: application.contact_person || '',
    contact_position: application.contact_position || '',
    next_meeting_date: application.next_meeting_date || '',
    meeting_notes: application.meeting_notes || '',
  });

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.patch(`/crm/applications/${application.id}`, {
        partnership_tier: form.partnership_tier || null,
        contract_value: form.contract_value ? parseFloat(form.contract_value) : null,
        contract_start: form.contract_start || null,
        contract_end: form.contract_end || null,
        selected_benefits: form.selected_benefits.length > 0 ? form.selected_benefits : null,
        sponsorship_goal: form.sponsorship_goal || null,
        event_interest: form.event_interest || null,
        contact_person: form.contact_person || null,
        contact_position: form.contact_position || null,
        next_meeting_date: form.next_meeting_date || null,
        meeting_notes: form.meeting_notes || null,
      });
      onSaved();
    } catch { /* */ }
    setSaving(false);
  };

  const toggleBenefit = (b: string) => {
    setForm(f => ({
      ...f,
      selected_benefits: f.selected_benefits.includes(b)
        ? f.selected_benefits.filter((x: string) => x !== b)
        : [...f.selected_benefits, b],
    }));
  };

  return (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      <div className="bg-gradient-to-r from-lime-600 to-green-600 px-5 py-3 flex items-center gap-2">
        <Trophy size={18} className="text-white" />
        <h3 className="text-white font-bold text-sm">Hutnik Business Club — Partnerstwo</h3>
      </div>
      <div className="p-5 space-y-4">
        {/* Partnership tier */}
        <div>
          <label className="text-xs font-medium text-gray-600 mb-2 block">Poziom partnerstwa</label>
          <div className="space-y-1.5">
            {PARTNERSHIP_TIERS.map(t => (
              <button
                key={t.value}
                onClick={() => setForm(f => ({ ...f, partnership_tier: f.partnership_tier === t.value ? '' : t.value }))}
                className={`w-full flex items-center justify-between px-3 py-2 rounded-lg border text-sm transition-colors ${
                  form.partnership_tier === t.value
                    ? 'border-lime-400 bg-lime-50 ring-1 ring-lime-400'
                    : 'border-gray-200 hover:border-lime-300'
                }`}
              >
                <div className="flex items-center gap-2">
                  <span className={`w-3 h-3 rounded-full ${t.color}`} />
                  <span className="font-medium text-gray-800">{t.label}</span>
                </div>
                <span className="text-xs text-gray-500">{t.price}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Contract info */}
        <div className="bg-gray-50 rounded-lg p-3 space-y-2">
          <div className="flex items-center gap-1.5 mb-1">
            <Handshake size={14} className="text-gray-600" />
            <span className="text-xs font-bold text-gray-700">Umowa</span>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div>
              <label className="text-xs text-gray-500">Wartość (PLN)</label>
              <input type="number" value={form.contract_value} onChange={e => setForm(f => ({ ...f, contract_value: e.target.value }))} className="w-full border rounded px-2 py-1 text-sm" placeholder="0" />
            </div>
            <div>
              <label className="text-xs text-gray-500">Od</label>
              <input type="date" value={form.contract_start} onChange={e => setForm(f => ({ ...f, contract_start: e.target.value }))} className="w-full border rounded px-2 py-1 text-sm" />
            </div>
            <div>
              <label className="text-xs text-gray-500">Do</label>
              <input type="date" value={form.contract_end} onChange={e => setForm(f => ({ ...f, contract_end: e.target.value }))} className="w-full border rounded px-2 py-1 text-sm" />
            </div>
          </div>
        </div>

        {/* Benefits */}
        <div>
          <label className="text-xs font-medium text-gray-600 mb-2 block flex items-center gap-1">
            <Megaphone size={12} />
            Wybrane świadczenia
          </label>
          <div className="flex flex-wrap gap-1.5">
            {BENEFIT_OPTIONS.map(b => (
              <button
                key={b}
                onClick={() => toggleBenefit(b)}
                className={`px-2.5 py-1 rounded-full text-xs border transition-colors ${
                  form.selected_benefits.includes(b)
                    ? 'bg-lime-100 text-lime-700 border-lime-300 font-medium'
                    : 'bg-gray-50 text-gray-500 border-gray-200 hover:border-lime-200'
                }`}
              >
                {b}
              </button>
            ))}
          </div>
          {form.selected_benefits.length > 0 && (
            <p className="text-xs text-gray-400 mt-1">Wybrano: {form.selected_benefits.length} świadczeń</p>
          )}
        </div>

        {/* Sponsorship goal */}
        <div>
          <label className="text-xs font-medium text-gray-600 mb-1 block flex items-center gap-1">
            <Target size={12} />
            Cel sponsoringu
          </label>
          <textarea value={form.sponsorship_goal} onChange={e => setForm(f => ({ ...f, sponsorship_goal: e.target.value }))} rows={2} className="w-full border rounded-lg px-3 py-2 text-sm resize-none" placeholder="np. Budowanie marki w regionie, CSR, employer branding..." />
        </div>

        {/* Contact & Meeting */}
        <div className="bg-blue-50 rounded-lg p-3 space-y-2 border border-blue-200">
          <div className="flex items-center gap-1.5 mb-1">
            <Calendar size={14} className="text-blue-600" />
            <span className="text-xs font-bold text-blue-700">Kontakt i spotkania</span>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-xs text-gray-500">Osoba kontaktowa</label>
              <input type="text" value={form.contact_person} onChange={e => setForm(f => ({ ...f, contact_person: e.target.value }))} className="w-full border rounded px-2 py-1 text-sm" />
            </div>
            <div>
              <label className="text-xs text-gray-500">Stanowisko</label>
              <input type="text" value={form.contact_position} onChange={e => setForm(f => ({ ...f, contact_position: e.target.value }))} className="w-full border rounded px-2 py-1 text-sm" placeholder="Prezes, Dyrektor mktg..." />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-xs text-gray-500">Zainteresowanie eventami</label>
              <input type="text" value={form.event_interest} onChange={e => setForm(f => ({ ...f, event_interest: e.target.value }))} className="w-full border rounded px-2 py-1 text-sm" placeholder="Gale, mecze, turnieje..." />
            </div>
            <div>
              <label className="text-xs text-gray-500">Następne spotkanie</label>
              <input type="date" value={form.next_meeting_date} onChange={e => setForm(f => ({ ...f, next_meeting_date: e.target.value }))} className="w-full border rounded px-2 py-1 text-sm" />
            </div>
          </div>
          <div>
            <label className="text-xs text-gray-500">Notatki ze spotkań</label>
            <textarea value={form.meeting_notes} onChange={e => setForm(f => ({ ...f, meeting_notes: e.target.value }))} rows={2} className="w-full border rounded px-2 py-1 text-sm resize-none" placeholder="Podsumowanie rozmów, ustalenia..." />
          </div>
        </div>

        <button onClick={handleSave} disabled={saving} className="w-full bg-lime-600 text-white py-2 rounded-lg font-medium hover:bg-lime-700 disabled:opacity-50 flex items-center justify-center gap-2">
          {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
          {saving ? 'Zapisywanie...' : 'Zapisz dane partnerstwa'}
        </button>
      </div>
    </div>
  );
};
